import { useEffect, useRef, useState } from "react";
import { Camera, X, Loader2, Smartphone } from "lucide-react";

/**
 * Fix BO (2026-11-03): "Tomar foto" sin salir de la app.
 * Con <input capture>, Android abre la app de cámara y puede cerrar el navegador por memoria: al volver se
 * perdía lo cargado. Con getUserMedia la foto se saca acá mismo. Si la cámara no abre (permiso, otra app
 * usándola), el vigilador puede usar la cámara del celular con un toque (onFallback necesita ese toque:
 * el navegador no deja abrir el selector de archivos sin un gesto del usuario).
 */
export function InAppCamera({ onCapture, onFallback, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const v = videoRef.current;
        if (v) { v.srcObject = stream; await v.play().catch(() => {}); }
        setReady(true);
      } catch (e) {
        if (cancelled) return;
        const det = String((e && (e.name || e.message)) || "");
        setError(/NotAllowed|Permission/i.test(det)
          ? "El navegador no tiene permiso para usar la cámara."
          : "No se pudo abrir la cámara dentro de la app.");
      }
    })();
    return () => {
      cancelled = true;
      if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    };
  }, []);

  const capture = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth || busy) return;
    setBusy(true);
    // Máximo 1920 px de lado: suficiente para evidencia y liviano para subir con datos móviles.
    const scale = Math.min(1, 1920 / Math.max(v.videoWidth, v.videoHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(v.videoWidth * scale);
    canvas.height = Math.round(v.videoHeight * scale);
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    if (!blob) { setBusy(false); setError("No se pudo sacar la foto."); return; }
    await onCapture(new File([blob], `foto-${Date.now()}.jpg`, { type: "image/jpeg" }));
    setBusy(false);
  };

  const barBtn = { display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 14px", fontSize: 12, fontWeight: 800,
    border: "1px solid rgba(255,251,244,0.4)", background: "transparent", color: "#FFFBF4", borderRadius: 6, cursor: "pointer" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "#000", display: "flex", flexDirection: "column" }} data-testid="inapp-camera">
      <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
        <video ref={videoRef} playsInline muted autoPlay style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        {!ready && !error && (
          <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "#FFFBF4" }}>
            <Loader2 size={28} className="spin" />
          </div>
        )}
        {error && (
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, padding: 24, textAlign: "center", color: "#FFFBF4" }} data-testid="inapp-camera-error">
            <div style={{ fontSize: 14, fontWeight: 700 }}>{error}</div>
            <button type="button" onClick={onFallback} className="btn-primary" style={{ padding: "12px 18px" }} data-testid="inapp-camera-fallback">
              <Smartphone size={14} style={{ marginRight: 6 }} /> Usar la cámara del celular
            </button>
          </div>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "14px 16px calc(14px + env(safe-area-inset-bottom))", background: "#0A0A0A" }}>
        <button type="button" onClick={onClose} style={barBtn} data-testid="inapp-camera-cancel"><X size={14} /> Cancelar</button>
        <button type="button" onClick={capture} disabled={!ready || busy} data-testid="inapp-camera-shoot" aria-label="Sacar foto"
          style={{ width: 68, height: 68, borderRadius: "50%", border: "4px solid #FFFBF4", background: busy ? "#BF3437" : "#FFFBF4", display: "grid", placeItems: "center", cursor: "pointer" }}>
          {busy ? <Loader2 size={22} className="spin" color="#FFFBF4" /> : <Camera size={24} color="#0A0A0A" />}
        </button>
        <button type="button" onClick={onFallback} style={barBtn} data-testid="inapp-camera-native"><Smartphone size={14} /> Cámara del celular</button>
      </div>
    </div>
  );
}
